/**
 * Cumulative cost counter shown beside the TokenCounter in the chat
 * footer. Renders a muted pill like ``≈ $0.0241`` so a user can watch
 * the bill for *this* chatroom accrue in real time — the cost half of
 * the cost-optimization guardrails (the token pill answers "how much
 * context?", this answers "how much money?").
 *
 * Totals are server-accumulated at the conversation level (mirroring the
 * token buckets), hydrated on conversation open and *set* from the
 * absolute ``cost_totals`` snapshot on every ``result`` event — so the
 * figure survives reloads and the message-window pruning that would make
 * a client-side per-message sum undercount.
 *
 * Hides itself on a fresh / free conversation — a $0 chat doesn't need to
 * announce its bill.
 */

import { useState } from "react";
import { useChatStore } from "@/stores/chatStore";
import { CostBreakdownPanel } from "./CostBreakdownPanel";

/**
 * Format a USD amount with magnitude-appropriate precision: cents-level
 * for dollar amounts, finer for the sub-cent totals a short chat racks
 * up. Kept honest — no rounding a real cost down to ``$0.00``.
 */
function formatUsd(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

export function CostCounter() {
  const cost = useChatStore((s) => s.totalCostUsd);
  const pplx = useChatStore((s) => s.totalPplxCostUsd);
  const conversationId = useChatStore((s) => s.activeConversationId);
  const [open, setOpen] = useState(false);

  const total = cost + pplx;
  if (total <= 0) return null;

  // The footer pill shows the running total; clicking opens the per-model
  // breakdown (provider-reported, fetched on demand from the usage ledger).
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => conversationId && setOpen((v) => !v)}
        disabled={!conversationId}
        className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-space-20 text-[11px] font-medium text-space-50 tabular-nums hover:text-space transition-colors disabled:cursor-default"
        title="Cost for this chat — click for the per-model breakdown"
        aria-label={`Conversation cost ${formatUsd(total)} — open breakdown`}
        aria-expanded={open}
      >
        <span className="text-space-40" aria-hidden>≈</span>
        {formatUsd(total)}
      </button>
      {open && conversationId && (
        <CostBreakdownPanel
          conversationId={conversationId}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
